import React, { useRef, useState } from 'react';
import { Container } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import Drawer from '../../components/Drawer/Drawer';
import { colors } from '../../styles/colors';
import { FontFamily, FontWeight } from '../../styles/typography';
import ArrowDownIcon from '../../icons/ArrowDownIcon';
import CloseIcon from '../../icons/CloseIcon';
import type {
  PartyListSortBy,
  SortDirection,
} from '../../components/PartyList/partyListModel';

type SortOption = {
  value: PartyListSortBy;
  label: string;
};

type MobilePartySortControlsProps = {
  count: number;
  options: SortOption[];
  sorting: PartyListSortBy;
  sortDirection: SortDirection;
  onSelectSort: (sorting: PartyListSortBy) => void;
  onToggleDirection: () => void;
};

function SortDirectionIcon({ direction }: { direction: SortDirection }) {
  return (
    <svg
      aria-hidden
      focusable="false"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: direction === 'desc' ? 'scaleY(-1)' : undefined,
        transition: 'transform 120ms ease',
      }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.3601 12.6031C14.6525 12.2606 14.7214 11.8298 14.5753 11.4624C14.429 11.0944 14.0825 10.8292 13.6327 10.785C13.3582 10.758 13.0375 10.733 12.6659 10.713C12.61 8.97199 12.4702 7.82519 12.3702 7.19719C12.2948 6.72416 11.96 6.29209 11.4115 6.24039C11.2538 6.22552 11.063 6.21612 10.8332 6.21682C10.6153 6.21749 10.4337 6.22659 10.283 6.24046C9.72774 6.29146 9.39737 6.73176 9.32887 7.20809C9.23864 7.83539 9.11034 8.97832 9.04687 10.709C8.65267 10.7292 8.31604 10.7553 8.03091 10.7836C7.58557 10.8278 7.24227 11.0896 7.09811 11.4551C6.95431 11.8195 7.02487 12.2453 7.31327 12.5841C8.13087 13.5447 9.14437 14.5088 9.97811 15.2527C10.4725 15.6939 11.2171 15.6939 11.7116 15.2526C12.5398 14.5136 13.5454 13.5574 14.3601 12.6031ZM7.96797 5.21639C8.41331 5.17219 8.75657 4.91036 8.90077 4.54489C9.04454 4.18046 8.97401 3.75472 8.68561 3.41589C7.86801 2.45527 6.85451 1.4912 6.02077 0.747296C5.52634 0.306101 4.78174 0.306123 4.28727 0.74733C3.45907 1.48637 2.45347 2.44262 1.63877 3.39689C1.34634 3.73939 1.27751 4.17016 1.42357 4.53752C1.56991 4.90552 1.91634 5.17079 2.36614 5.21496C2.64067 5.24192 2.96141 5.26699 3.33301 5.28696C3.38887 7.02799 3.52861 8.17479 3.62871 8.80276C3.70407 9.27582 4.03884 9.70789 4.58734 9.75959C4.74501 9.77446 4.93587 9.78386 5.16571 9.78316C5.38361 9.78249 5.56514 9.77336 5.71591 9.75952C6.27114 9.70852 6.60151 9.26822 6.67001 8.79186C6.76024 8.16459 6.88854 7.02166 6.95201 5.29102C7.34621 5.27076 7.68284 5.24466 7.96797 5.21639Z"
        fill="#165050"
      />
    </svg>
  );
}

function CheckmarkIcon() {
  return (
    <svg
      aria-hidden
      focusable="false"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.7537 5.71081C13.1821 5.10282 13.9955 4.77113 14.7058 5.18184C15.141 5.43356 15.6432 5.8111 16.043 6.35626C16.4297 6.88359 16.3248 7.56214 16.0069 8.04998C13.746 11.5191 12.0461 13.7795 10.9827 15.1026C10.1922 16.0861 8.77917 16.1581 7.88312 15.275C6.90683 14.3127 5.54224 12.8827 4.12945 11.1317C3.70039 10.6 3.5873 9.81802 4.04146 9.23406C4.33876 8.85186 4.672 8.52276 4.97632 8.25801C5.64554 7.67567 6.6052 7.82254 7.17765 8.43806C7.8908 9.20496 8.46188 9.85544 8.8551 10.3144C9.01395 10.4997 9.14373 10.6541 9.24217 10.7722L9.30473 10.6801C9.47384 10.4319 9.7232 10.0672 10.0438 9.60129C10.6849 8.66947 11.6118 7.33225 12.7537 5.71081Z"
        fill="#165050"
      />
    </svg>
  );
}

function MobilePartySortControls({
  count,
  options,
  sorting,
  sortDirection,
  onSelectSort,
  onToggleDirection,
}: MobilePartySortControlsProps) {
  const [isSortDrawerOpen, setSortDrawerOpen] = useState(false);
  const sortButtonRef = useRef<HTMLButtonElement>(null);
  const selectedSortLabel =
    options.find((option) => option.value === sorting)?.label || options[0].label;

  return (
    <>
      <Container
        sx={{
          mb: rem(16),
          display: ['flex', 'flex', 'none'],
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: rem(12),
          px: [0, null, 0],
        }}
      >
        <h2
          sx={{
            m: 0,
            fontFamily: FontFamily.Heading,
            fontSize: rem(16),
            fontWeight: FontWeight.Medium,
            lineHeight: rem(24),
            letterSpacing: 0,
            color: colors.base.Text.Primary,
            span: {
              fontFamily: FontFamily.Body,
              fontWeight: FontWeight.Normal,
              color: colors.base.Text.Secondary,
            },
          }}
        >
          Partier <span>({count})</span>
        </h2>
        <div
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(8),
          }}
        >
          <button
            ref={sortButtonRef}
            type="button"
            aria-label={`Sorter efter ${selectedSortLabel}`}
            onClick={() => setSortDrawerOpen(true)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: rem(8),
              height: rem(32),
              px: rem(14),
              border: '1px solid rgba(31, 111, 111, 0.20)',
              borderRadius: rem(80),
              backgroundColor: colors.base.Background.White,
              color: colors.base.Text.Primary,
              fontFamily: FontFamily.Heading,
              fontSize: rem(14),
              fontWeight: FontWeight.Medium,
              lineHeight: rem(20),
            }}
          >
            {selectedSortLabel}
            <ArrowDownIcon
              sx={{
                color: colors.base.Icon.BrandStatic,
                width: rem(12),
                height: rem(12),
              }}
            />
          </button>
          <button
            type="button"
            onClick={onToggleDirection}
            aria-label="Skift sorteringsretning"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: rem(32),
              height: rem(32),
              border: `1px solid rgba(31, 111, 111, 0.20)`,
              borderRadius: '50%',
              backgroundColor: colors.base.Background.White,
            }}
          >
            <SortDirectionIcon direction={sortDirection} />
          </button>
        </div>
      </Container>
      <Drawer
        isOpen={isSortDrawerOpen}
        onOpenChange={setSortDrawerOpen}
        title="Sortér efter"
        finalFocus={sortButtonRef}
      >
        <div
          sx={{
            px: rem(20),
            pt: rem(24),
            pb: rem(32),
          }}
        >
          <div
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: rem(20),
            }}
          >
            <h2
              sx={{
                m: 0,
                fontFamily: FontFamily.Heading,
                fontSize: rem(16),
                fontWeight: FontWeight.Medium,
                lineHeight: rem(24),
                letterSpacing: 0,
                color: colors.base.Text.Primary,
              }}
            >
              Sortér efter
            </h2>
            <button
              type="button"
              onClick={() => setSortDrawerOpen(false)}
              aria-label="Luk sortering"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: rem(32),
                height: rem(32),
                p: 0,
                border: 'none',
                backgroundColor: 'transparent',
                color: colors.base.Icon.BrandStatic,
              }}
            >
              <CloseIcon width={16} height={16} />
            </button>
          </div>
          <div sx={{ display: 'grid', gap: rem(12) }}>
            {options.map((option) => {
              const isSelected = sorting === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onSelectSort(option.value);
                    setSortDrawerOpen(false);
                  }}
                  aria-pressed={isSelected}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: rem(42),
                    px: rem(48),
                    border: isSelected
                      ? `1px solid ${colors.base.Border.Brand}`
                      : '1px solid transparent',
                    borderRadius: rem(80),
                    backgroundColor: isSelected
                      ? colors.components.fixedTop.Background.Default
                      : colors.components.partyListItem.Background.Default,
                    color: colors.base.Text.Primary,
                    fontFamily: FontFamily.Body,
                    fontSize: rem(16),
                    fontWeight: FontWeight.Normal,
                    lineHeight: rem(24),
                  }}
                >
                  {isSelected ? (
                    <span
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        left: rem(28),
                        display: 'inline-flex',
                        width: rem(20),
                        height: rem(20),
                      }}
                    >
                      <CheckmarkIcon />
                    </span>
                  ) : null}
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default MobilePartySortControls;
